import EditableContent from "@/components/DataTable/components/DataTableContainer/Components/DataTableBody/Components/EditableContent";
import StyledTableCell from "@/components/DataTable/components/DataTableContainer/Components/StyledTableCell";
import { Software } from "@/services/global-services/models";
import lightTheme from "@/views/AdminView/theme/light";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    ThemeProvider,
    Typography,
    useTheme,
} from "@mui/material";
import { KeyboardEvent, useEffect, useState } from "react";
import { DataTableColumnDefinition } from "../../../DataTable/Models";
import { InformationTableBlockData } from "./InformationTableBlockTool";

export interface InformationTableBlockComponentProps {
    data: InformationTableBlockData;
    defaults: InformationTableBlockData;
    software: Software[];
    onChange: (data: InformationTableBlockData) => void;
}

export const InformationTableBlockComponent = ({
    data,
    defaults,
    software,
    onChange,
}: InformationTableBlockComponentProps) => {
    const theme = useTheme();
    const [initialized, setInitialized] = useState<boolean>(false);

    const [initialTitle, setInitialTitle] = useState<string>("");
    const [initialColumns, setInitialColumns] = useState<DataTableColumnDefinition[]>([]);
    const [initialCaption, setInitialCaption] = useState<string>("");

    const [title, setTitle] = useState<string>("");
    const [columns, setColumns] = useState<DataTableColumnDefinition[]>([]);
    const [caption, setCaption] = useState<string>("");

    useEffect(() => {
        setTitle(data.title ?? "");
        setCaption(data.caption ?? "");
        setColumns(data.columns.map((x) => ({ ...x })));

        setInitialTitle(data.title ?? "");
        setInitialCaption(data.caption ?? "");
        setInitialColumns(data.columns.map((x) => ({ ...x })));

        setInitialized(true);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const data = {
            title,
            columns,
            caption,
            hasSoftwareColumns: true,
        };

        if (initialized) onChange(data);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, columns, caption]);

    const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
        if ((e.code === "Enter" && !e.shiftKey) || e.code === "NumpadEnter") {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    return initialized ? (
        <ThemeProvider theme={lightTheme}>
            <TableContainer component={Paper} onClick={(e) => e.preventDefault()}>
                <Box
                    sx={{
                        position: "sticky",
                        left: 0,
                        right: 0,
                        display: "flex",
                        flex: 1,
                        backgroundColor: lightTheme.palette.primary.main,
                        color: lightTheme.palette.common.white,
                        placeContent: "center",
                        padding: 2,
                    }}
                >
                    <Typography
                        component={EditableContent}
                        variant="body2"
                        contentEditable
                        sx={{
                            textTransform: "uppercase",
                            fontWeight: 700,
                            cursor: "text",
                            minWidth: "50%",
                            textAlign: "center",
                            p: 0,
                            "&:before": {
                                color: "white !important",
                                opacity: 0.5,
                            },
                        }}
                        suppressContentEditableWarning={true}
                        onInput={(e) => setTitle(e.currentTarget.innerHTML)}
                        dangerouslySetInnerHTML={{ __html: initialTitle || "" }}
                        data-edit-placeholder="Optional table title..."
                        onKeyDown={onKeyDown}
                    />
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            {initialColumns.map((column, index) => {
                                const onChange = (value: string) => {
                                    const columnData = [...columns];
                                    let newValue = value.trim();
                                    if (newValue.length === 0) newValue = defaults.columns[index].value.trim();
                                    columnData[index].value = newValue;
                                    setColumns(columnData);
                                };

                                return (
                                    <StyledTableCell
                                        key={index}
                                        sx={index === 0 ? { borderLeft: "none !important" } : {}}
                                    >
                                        <Typography
                                            component={EditableContent}
                                            variant="body2"
                                            contentEditable
                                            sx={{
                                                textTransform: "uppercase",
                                                fontWeight: 700,
                                                whiteSpace: "pre-wrap",
                                                p: 0,
                                            }}
                                            suppressContentEditableWarning={true}
                                            onInput={(e) => onChange(e.currentTarget.innerHTML)}
                                            dangerouslySetInnerHTML={{ __html: column.value || "" }}
                                            data-edit-placeholder={defaults.columns[index].value}
                                            onKeyDown={onKeyDown}
                                        />
                                    </StyledTableCell>
                                );
                            })}
                            {software
                                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name))
                                .map((software) => (
                                    <StyledTableCell key={software.id}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textTransform: "uppercase",
                                                fontWeight: 700,
                                                whiteSpace: "nowrap",
                                                p: 0,
                                                opacity: 0.5,
                                                cursor: "default",
                                            }}
                                        >
                                            {software.name}
                                        </Typography>
                                    </StyledTableCell>
                                ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <StyledTableCell
                                colSpan={columns.length + software.length}
                                sx={{ borderBottom: "unset", pb: 0 }}
                            >
                                <Typography variant={"body2"} sx={{ p: 0, m: 2, flex: 1, textAlign: "center" }}>
                                    <Box component={"span"} sx={{ cursor: "default", opacity: 0.6 }}>
                                        Table content will be displayed here.
                                    </Box>
                                </Typography>
                            </StyledTableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ minHeight: 20, display: "flex" }}>
                <Typography
                    variant={"caption"}
                    component={EditableContent}
                    contentEditable
                    sx={{
                        flex: 1,
                        p: 0,
                        m: theme.spacing(0.5, 0.5, 2),
                        display: "inline-block",
                        cursor: "text",
                    }}
                    suppressContentEditableWarning={true}
                    onInput={(e) => setCaption(e.currentTarget.innerHTML)}
                    dangerouslySetInnerHTML={{ __html: initialCaption || "" }}
                    data-edit-placeholder="Optional default table caption..."
                    onKeyDown={onKeyDown}
                />
            </Box>
        </ThemeProvider>
    ) : null;
};
